import { Group, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import {
  CancelModal,
  DEFAULT_PAGE_SIZE,
  Loader,
  SuccessModal,
  Switch,
  Button,
  ColumnManager,
} from "@tfg/ui"
import _ from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { TraceabilityStatus } from "@tfg/types/endpoints/traceability"

import { queryClient } from "~/backend/queryClient"
import { InlineBanner } from "~/components/InlineBanner"
import { ProductFilters } from "~/components/ProductFilters"
import { ProductSearch } from "~/components/ProductSearch"
import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { CustomTable } from "~/components/table/CustomTable"
import {
  FindProductsTraceabilityRequestFiltersDto,
  findTraceabilityJobsByStatusHttpControllerFindTraceabilityJobsByStatusPathParamsStatusEnum as TraceabilityJobStatusEnum,
  FindProductsTraceabilityRequestSortsDto,
  findTraceabilityJobsByStatusQueryKey,
  getTraceabilityCampaignStatsQueryKey,
  useFindProductsTraceability,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { useGetOrganizationSettingsQuery } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/hooks/useGetOrganizationSettingsQuery"
import { LaunchTraceabilityModal } from "~/pages/Brand/Products/List/Components/LaunchTraceabilityModal"
import { ProductTraceabilitySorts } from "~/pages/Brand/Products/List/Components/ProductsTables/traceability/ProductTraceabilitySorts"
import { useTraceabilityTable } from "~/pages/Brand/Products/List/Components/ProductsTables/traceability/useTraceabilityTable"
import { ProductsListView } from "~/pages/Brand/Products/List/constants.ts"
import { useAuth } from "~/store/auth-context.tsx"

export const TraceabilityProductsTable = () => {
  const view = ProductsListView.TRACEABILITY

  const [traceabilityModalOpened, traceabilityModalActions] =
    useDisclosure(false)

  const [cancelModalOpened, cancelModalActions] = useDisclosure(false)
  const [successModalOpened, successModalActions] = useDisclosure(false)

  const [showOnlyMainFabric, setShowOnlyMainFabric] = useState<boolean>(false)
  const { t } = useTranslation(["products", "common", "traceability"])
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { user, isAdmin } = useAuth()
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const { data: org } = useGetOrganizationSettingsQuery(user?.organization?.id)

  const [filtersValue, setFiltersValue] = useSearchParamsFilters(
    z.custom<FindProductsTraceabilityRequestFiltersDto>()
  )

  const [pageCount, setPageCount] = useState<number>(0)

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const [shouldKeepPulling, setShouldKeepPulling] = useState<boolean>(false)

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const fetchDataOptions = {
    pageIndex,
    pageSize,
  }

  const [sorts, setSorts] = useState<FindProductsTraceabilityRequestSortsDto>(
    {}
  )

  const {
    data: productsData,
    isPending: isLoadingProducts,
    isFetching: isFetchingProducts,
  } = useFindProductsTraceability(
    {
      filters: filtersValue,
      sorts,
      pagination: {
        pageIndex: fetchDataOptions.pageIndex,
        pageSize: fetchDataOptions.pageSize,
      },
    },
    {
      ...(shouldKeepPulling ? { query: { refetchInterval: 5000 } } : {}),
    }
  )

  useEffect(() => {
    const products = productsData?.data.products ?? []

    setShouldKeepPulling(
      products.some((p) => p.traceability.status === TraceabilityStatus.PENDING)
    )
  }, [productsData])

  useEffect(() => {
    if (productsData?.data.totalProducts === 0) {
      setPageCount(0)
    }
    if (productsData?.data.totalProducts) {
      setPageCount(Math.ceil(productsData?.data.totalProducts / pageSize))
    }
  }, [productsData?.data.totalProducts, pageSize])

  const products = useMemo(() => {
    return productsData?.data.products ?? []
  }, [productsData])

  const deepestRank =
    _.max(products.map((p) => p.operationTree?.maxRank ?? 0)) ?? 0

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [filtersValue])

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const table = useTraceabilityTable({
    setPagination,
    setRowSelection,
    pagination,
    rowSelection,
    pageCount,
    maxRank: deepestRank,
    showOnlyMainFabric,
    products,
  })

  let adminMessage: string | null = null

  if (org?.settings?.zalandoPlan === "ENABLED") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InlineBanner
          type="info"
          size="xs"
          message={t("simulation.notSubscribed.title")}
        >
          {t("simulation.notSubscribed.message")}
        </InlineBanner>
      )
    }
  }

  return (
    <>
      <Stack>
        {adminMessage && (
          <InlineBanner
            type="admin"
            size="xs"
            message={t("simulation.notSubscribed.title")}
          >
            {adminMessage}
          </InlineBanner>
        )}
        <Group justify="space-between">
          <Group>
            <ProductSearch
              filtersValue={filtersValue}
              setFiltersValue={setFiltersValue}
              isFetching={isFetchingProducts}
            />
            <ProductTraceabilitySorts sorts={sorts} setSorts={setSorts} />
            <ProductFilters<FindProductsTraceabilityRequestFiltersDto>
              setFiltersValue={setFiltersValue}
              filtersValue={filtersValue}
              extraFilters={[
                {
                  label: t("lists.placeholders.duplicateSites"),
                  key: "duplicateSites",
                  options: [
                    { label: t("lists.placeholders.all"), value: "all" },
                    {
                      value: "redundant",
                      label: t("lists.placeholders.redundant"),
                    },
                  ],
                  type: "select",
                },
              ]}
            />
            <ColumnManager
              table={table}
              translations={columnManagerTranslations}
              isOpen={isColumnManagerOpen}
              setIsOpen={setIsColumnManagerOpen}
            />
            <Switch
              label={t("common:showOnlyMainFabric")}
              checked={showOnlyMainFabric}
              onChange={() => setShowOnlyMainFabric((v) => !v)}
            />
          </Group>
          {isAdmin && productsData?.data.totalProducts ? (
            <Button
              onClick={() => {
                const queryKeys = [
                  getTraceabilityCampaignStatsQueryKey(),
                  findTraceabilityJobsByStatusQueryKey(
                    TraceabilityJobStatusEnum.RUNNING
                  ),
                ]
                queryKeys.forEach((queryKey) => {
                  queryClient.invalidateQueries({ queryKey })
                })
                traceabilityModalActions.open()
              }}
              toolTip={t("traceability.action.tooltip")}
              disabled={productsData?.data.totalProducts === 0}
            >
              {t("traceability.action.label")}
            </Button>
          ) : null}
        </Group>
        {isLoadingProducts ? (
          <Loader fullPage />
        ) : (
          <CustomTable
            table={table}
            view={view}
            manualPaginationCount={productsData?.data.totalProducts || 0}
            stickyHeader
            layout="fixed"
            setOpenColumnManager={setIsColumnManagerOpen}
          />
        )}
      </Stack>
      <LaunchTraceabilityModal
        opened={traceabilityModalOpened}
        cancelModalOpened={cancelModalOpened}
        onClose={traceabilityModalActions.close}
        onSuccess={successModalActions.open}
        onCancel={cancelModalActions.open}
      />
      <CancelModal
        opened={cancelModalOpened}
        onClose={cancelModalActions.close}
        onCancel={traceabilityModalActions.close}
        title={t("traceability:launch.modal.cancel.title")}
        yes={t("traceability:launch.modal.cancel.yes")}
        no={t("traceability:launch.modal.cancel.no")}
      />
      <SuccessModal
        opened={successModalOpened}
        onClose={successModalActions.close}
        title={t("traceability:launch.modal.success.title")}
        description={t("traceability:launch.modal.success.description")}
        successButtonLabel={t("traceability:launch.modal.success.button")}
      />
    </>
  )
}
